import React, {Suspense, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {v4 as uuidv4} from 'uuid';
import {CircularProgress} from "@mui/material";
import ApiClient from '../../service_clients/ApiClient';
import NobiEventTracker from '../../utils/NobiEventTracker';
import Events from '../../utils/Events';
import Chatbot from "../Chatbot/Chatbot";
import styles from './CartAssistantInjector.module.css';
import {EntryPoint as EntryPoint} from "../Chatbot/EntryPoint";
import GlowOnVisible from "../GlowOnVisible/GlowOnVisible";

const CartAssistantInjector = ({
    merchant,
    assistantConfiguration,
    brandTheme,
    log,
    onSendMessage,
    cartProductVariantInternalId,
    cartProductInternalId,
    setCartProductVariantInternalId,
    setCartProductInternalId }) => {

    const MaxTotalCollectionsToRecommend = 2;
    const MaxTotalProductsPerCollection = 2;

    const [icebreakerResponse, setIcebreakerResponse] = useState(null);
    const [recommendedCollections, setRecommendedCollections] = useState(null);
    const [allProductIdsInCart, setAllProductIdsInCart] = useState([]);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);

    const containerRef = useRef(null);

    const totalCollectionsToRecommend = Math.floor(Math.random() * MaxTotalCollectionsToRecommend) + 1;
    const totalProductsPerCollection = Math.floor(Math.random() * MaxTotalProductsPerCollection) + 1;
    const showMessageForm = Math.random() < 0.5 || window.location.href.indexOf("localhost") > -1; // 50% chance to show the message form
    const showCommentary = Math.random() < 0.5 || window.location.href.indexOf("localhost") > -1; // 50% chance to show the commentary

    const fetchedProductInternalIds = useRef(new Set());

    function Heading({ level = 3, style, children }) {
        const Tag = `h${level}`;
        return <Tag style={style}>{children}</Tag>;
    }

    // Effect to check for preexisting cart items and subscribe to cart-addition events
    useEffect(() => {
        if (!assistantConfiguration?.cartAssistant) {
            log && log("No cart drawer selector provided in assistant configuration. Aborting CartAssistantInjector.");
            return;
        }

        injectStylesheetToHideExistingRecommendations();
        const handler = createMessageHandlerForProductsAddedToCart();
        return ()=> {
            window.removeEventListener("message", handler);
            log && log("Unsubscribed from cart addition events");
        }
    }, [assistantConfiguration]);

    // Effect to render the assistant card when the icebreaker response is available
    useEffect(() => {
        if (!icebreakerResponse && !recommendedCollections && !loadingRecommendations) {
            return;
        }

        if (!merchant || !assistantConfiguration?.cartAssistant) {
            return;
        }

        if (containerRef.current) {
            return;
        }

        const selector = assistantConfiguration.cartAssistant.containerSelector;
        if (!selector) {
            log && log("No cart drawer selector provided in assistant configuration. Aborting CartAssistantInjector.");
            return;
        }

        // 1) inject into any existing drawer(s)
        const cartDrawers = document.querySelectorAll(selector);
        if (cartDrawers.length === 0) {
            log && log("No cart drawer found. Aborting CartAssistantInjector.");
            return;
        }
        cartDrawers.forEach(cartDrawer => {
            if (cartDrawer) {
                injectCartAssistantIntoCartDrawer(cartDrawer);
            }
        });

        // 2) observe future drawer creations
        const observer = new MutationObserver(mutations => {
            for (const mutationRecords of mutations) {
                for (const node of mutationRecords.addedNodes) {
                    if (node.nodeType === 1 && node.matches(selector)) {
                        injectCartAssistantIntoCartDrawer(node);
                    }
                }
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            if (containerRef.current) {
                ReactDOM.unmountComponentAtNode(containerRef.current);
                containerRef.current.remove();
                containerRef.current = null;
            }
        }
    }, [icebreakerResponse, recommendedCollections, merchant, brandTheme, assistantConfiguration, onSendMessage]);

    function injectStylesheetToHideExistingRecommendations() {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = `
            ${assistantConfiguration?.cartAssistant?.existingRecommendationsSelector} {
                display: none !important;
            }
        `;
        document.head.appendChild(style);
        log && log("Injected stylesheet to hide existing recommendations");
    }

    // Function to add product IDs to the cart
    function addProductInCart(productId) {
        if (!productId || allProductIdsInCart.includes(productId)) {
            return;
        }

        setAllProductIdsInCart((prev) => [...prev, productId]);
        log && log("Added product ID to cart: " + productId);
    }

    function createMessageHandlerForProductsAddedToCart() {
        const handler = (event) => {
            let details = event.data;
            if (!details || !details.productInternalId || !details.productVariantInternalId || fetchedProductInternalIds.current.has(details.productInternalId)) {
                return;
            }

            log && log("Setting cart product internal ID: " + details.productInternalId + " and variant ID: " + details.productVariantInternalId);
            setCartProductInternalId(details.productInternalId);
            setCartProductVariantInternalId(details.productVariantInternalId);

            addProductInCart(details.productInternalId);
            fetchIcebreaker(details.productInternalId);
        }
        window.addEventListener("message", handler);
        log && log("Subscribed to cart addition events");
        return handler;
    }

    function openCartDrawer() {
        if (assistantConfiguration?.cartAssistant?.cartOpenButtonSelector) {
            // Some merchants do not open the cart when an item is added to the cart, so we do it automatically once we receive product recommendations
            const cartOpenButton = document.querySelector(assistantConfiguration.cartAssistant.cartOpenButtonSelector);
            if (cartOpenButton) {
                // Create a slight delay in opening the drawer so that it *feels* like it doesn't take so long for the recommendations to appear
                setTimeout(() => {
                    cartOpenButton.click();
                }, 1000);
            }
        }
    }

    // Function to fetch the icebreaker message given cart items
    const fetchIcebreaker = (productInternalId) => {
        if (!productInternalId || !merchant || fetchedProductInternalIds.current.has(productInternalId)) {
            return;
        }
        fetchedProductInternalIds.current.add(productInternalId);

        log && log("Fetching icebreaker message for product ID: " + productInternalId);
        const conversationId = uuidv4();

        openCartDrawer();

        // Choose randomly between 1-3 collections
        const recommendedProductsDto = ApiClient.buildRecommendedProductsRequestDto({
            conversationId: conversationId,
            productInternalId: productInternalId,
            includeIcebreakerMessage: false,
            numberOfCollections: totalCollectionsToRecommend,
            numberOfRecommendedProductsPerCollection: totalProductsPerCollection,
            existingCartProductInternalIds: allProductIdsInCart
        });

        let eventDetails = {
            conversationId: conversationId,
            productInternalId: productInternalId,
            numberOfCollections: totalCollectionsToRecommend,
            numberOfRecommendedProductsPerCollection: totalProductsPerCollection,
            numberOfProductsInCart: allProductIdsInCart.length,
            showMessageForm: showMessageForm,
            showCommentary: showCommentary
        }

        NobiEventTracker.trackEvent(
            merchant.id,
            eventDetails,
            EntryPoint.Cart,
            Events.RequestProductRecommendations
        );

        setLoadingRecommendations(true);
        ApiClient.getRecommendedProducts(
            merchant.id,
            recommendedProductsDto,
            handleRetrievedProductCollections,
            handleMessageChunkReceived,
            null,
            (error) => { log && log('Error fetching recommended products: ' + error);},
            null
        );
    };

    function handleMessageChunkReceived(chunk) {
        setLoadingRecommendations(false);
        log && log("Received message chunk: " + chunk);
        setIcebreakerResponse(prev => {
            if (prev) {
                return prev + chunk;
            }
            else {
                return chunk;
            }
        });
    }

    function handleRetrievedProductCollections(searchResultResponseDto) {
        setLoadingRecommendations(false);
        if (!searchResultResponseDto || !searchResultResponseDto.categories || searchResultResponseDto.categories.length === 0) {
            log && log("No recommended collections found");
            return;
        }

        setRecommendedCollections(searchResultResponseDto.categories);

        // Add all of the retrieved product internal IDs to fetchedProductInternalIds so that we don't re-inject the cart when someone Quick Adds an item
        searchResultResponseDto.categories.forEach(category => {
            if (category.productVariants && category.productVariants.length > 0) {
                category.productVariants.forEach(productVariant => {
                    fetchedProductInternalIds.current.add(productVariant.product.internalId);
                });
            }
        });
    }

    function sendMessageWithInitialCategories(iceBreaker, userMessage) {
        onSendMessage(iceBreaker, userMessage, recommendedCollections, EntryPoint.Cart);
    }

    function addVisibilityObserverForCartAssistant(cartDrawer) {
        let details = {
            conversationId: uuidv4(),
            productInternalId: cartProductInternalId,
            productVariantInternalId: cartProductVariantInternalId,
            numberOfProductsInCart: allProductIdsInCart.length,
            numberOfCollections: recommendedCollections?.length || 0,
            numberOfRecommendedProductsPerCollection: totalProductsPerCollection,
            showMessageForm: showMessageForm,
            showCommentary: showCommentary
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    NobiEventTracker.trackEvent(
                        merchant.id,
                        details,
                        EntryPoint.Cart,
                        Events.CartDrawerAssistantViewed
                    );
                    observer.disconnect();
                }
            });
        }, {
            root: cartDrawer,      // observe visibility relative to the cart drawer
            threshold: 0.1,
            rootMargin: '0px',
        });

        observer.observe(containerRef.current);
        return observer;
    }

    function injectCartAssistantIntoCartDrawer(cartDrawer) {
        if (!cartDrawer) {
            log && log("No cart drawer found. Aborting CartAssistantInjector.");
            return;
        }

        if (containerRef.current) {
            return;
        }

        // Create a container element to host the assistant card
        const container = document.createElement('div');
        container.id = "nobi-cart-assistant-container";
        containerRef.current = container;
        cartDrawer.appendChild(container);

        const observer = addVisibilityObserverForCartAssistant(cartDrawer);

        const headingLevel = assistantConfiguration?.cartAssistant?.headingLevel || 3;

        ReactDOM.render(
            <Suspense fallback={<div>Loading...</div>} ref={containerRef}>
                <div id={styles.cartAssistantContainer}>
                    {
                        assistantConfiguration?.cartAssistant?.header &&
                            <Heading level={headingLevel} style={brandTheme?.cartAssistant?.header}>{assistantConfiguration?.cartAssistant?.header}</Heading>
                    }
                    {loadingRecommendations &&
                        <div className={styles.loadingContainer}>
                            <CircularProgress size={25} />
                        </div>
                    }
                    {!loadingRecommendations &&
                        <GlowOnVisible root={cartDrawer}>
                            <div id={styles.cartAssistantChatbotWrapper}>
                                <Chatbot
                                    merchant={merchant}
                                    initialAssistantMessage={icebreakerResponse}
                                    brandTheme={brandTheme}
                                    isFirstMessageFromSearchBar={false}
                                    showSuggestions={false}
                                    entryPoint={EntryPoint.Cart}
                                    onSendMessage={onSendMessage ? sendMessageWithInitialCategories : null}
                                    initialCategories={recommendedCollections}
                                    assistantConfiguration={assistantConfiguration}
                                    cartProductVariantInternalId={cartProductVariantInternalId}
                                    showMessageForm={showMessageForm}
                                    showCommentary={showCommentary}
                                />
                            </div>
                        </GlowOnVisible>
                    }
                </div>
            </Suspense>,
            container
        );

        return () => {
            observer.disconnect();
        };
    }

    return null;  // This component doesn't render anything because it injects the assistant card into the cart drawer
};

export default CartAssistantInjector;
